/**
 * Client Detail Page - Complete client profile
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientsApi, ClientDetail } from '../api/clients';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: '#8c8c8c',
  AWAITING_PAYMENT: '#faad14',
  PAID: '#52c41a',
  LYRICS_GENERATION: '#1677ff',
  LYRICS_REVIEW: '#722ed1',
  LYRICS_APPROVED: '#52c41a',
  SONG_GENERATION: '#13c2c2',
  COMPLETED: '#52c41a',
  CANCELLED: '#ff4d4f',
};

const CHANNEL_ICONS: Record<string, string> = {
  telegram: '📱',
  facebook: '👤',
  instagram: '📷',
  viber: '💬',
  email: '✉️',
};

function getChannelIcon(channel: string) {
  return CHANNEL_ICONS[channel.toLowerCase()] || '💬';
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('uk-UA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadClient = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await clientsApi.getDetail(id);
      setClient(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Помилка завантаження');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClient();
  }, [id]);

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <div style={loadingBoxStyle}>Завантаження...</div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div style={{ padding: 24 }}>
        <div style={errorBoxStyle}>
          <div style={{ color: '#cf1322' }}>{error || 'Клієнта не знайдено'}</div>
          <button onClick={() => navigate(-1)} style={errorLinkStyle}>
            Назад
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => navigate(-1)} style={backBtnStyle}>
          ← Назад
        </button>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: '8px 0 4px' }}>
          {client.name}
        </h1>
        <p style={{ color: '#8c8c8c', margin: 0 }}>
          Клієнт з {formatDate(client.created_at)}
        </p>
      </div>

      <div style={gridStyle}>
        {/* Left column: Info, Stats, Channels */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Contact Info */}
          <Card title="Контактна інформація">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {client.email && (
                <Field label="Email" value={client.email} />
              )}
              {client.phone && (
                <Field label="Телефон" value={client.phone} />
              )}
              {!client.email && !client.phone && (
                <p style={{ color: '#8c8c8c', fontSize: 13, margin: 0 }}>
                  Контактні дані не вказані
                </p>
              )}
            </div>
          </Card>

          {/* Stats */}
          <Card title="Статистика">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <StatRow label="Замовлень" value={client.stats.total_orders} />
              <StatRow label="Активних розмов" value={client.stats.active_conversations} />
              <StatRow label="Каналів" value={client.stats.channels.length} />
            </div>
          </Card>

          {/* Channel Identities */}
          <Card title="Канали зв'язку">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {client.channel_identities.length === 0 ? (
                <div style={{ color: '#8c8c8c', fontSize: 13 }}>
                  Каналів немає
                </div>
              ) : (
                client.channel_identities.map((identity, index) => (
                  <div key={index} style={channelItemStyle}>
                    <span style={{ fontSize: 22 }}>
                      {getChannelIcon(identity.channel)}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, textTransform: 'capitalize' }}>
                        {identity.channel}
                      </div>
                      <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                        {identity.channel_username || identity.channel_user_id}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Right column: Orders & Conversations */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Orders */}
          <div style={{ ...cardOuterStyle, padding: 0 }}>
            <div style={cardHeaderStyle}>
              Замовлення ({client.orders.length})
            </div>
            <div>
              {client.orders.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>
                  Замовлень немає
                </div>
              ) : (
                client.orders.map((order, idx) => {
                  const color = STATUS_COLORS[order.status] || '#8c8c8c';
                  return (
                    <div
                      key={order.id}
                      onClick={() => navigate(`/orders/${order.id}`)}
                      style={{
                        padding: 16,
                        borderTop: idx === 0 ? 'none' : '1px solid #f0f0f0',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#fafafa')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 500 }}>
                          {order.id}
                        </div>
                        <Badge text={order.status} color={color} />
                      </div>
                      <div style={{ fontSize: 13, color: '#595959' }}>
                        Створено: {formatDate(order.created_at)}
                        {order.total_amount && (
                          <> · Сума: <strong>{order.total_amount} грн</strong></>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Conversations */}
          <div style={{ ...cardOuterStyle, padding: 0 }}>
            <div style={cardHeaderStyle}>
              Розмови ({client.conversations.length})
            </div>
            <div>
              {client.conversations.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>
                  Розмов немає
                </div>
              ) : (
                client.conversations.map((conversation, idx) => (
                  <div
                    key={conversation.id}
                    onClick={() => navigate(`/conversations/${conversation.id}`)}
                    style={{
                      padding: 16,
                      borderTop: idx === 0 ? 'none' : '1px solid #f0f0f0',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#fafafa')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18 }}>
                          {getChannelIcon(conversation.channel)}
                        </span>
                        <div style={{ fontWeight: 500, textTransform: 'capitalize' }}>
                          {conversation.channel}
                        </div>
                      </div>
                      <Badge
                        text={conversation.status === 'ACTIVE' ? 'Активна' : 'Закрита'}
                        color={conversation.status === 'ACTIVE' ? '#52c41a' : '#8c8c8c'}
                      />
                    </div>
                    <div style={{ fontSize: 13, color: '#595959' }}>
                      Створено: {formatDate(conversation.created_at)}
                      {conversation.last_message_at && (
                        <> · Активність: {formatDate(conversation.last_message_at)}</>
                      )}
                      {conversation.order_id && (
                        <> · Замовлення:{' '}
                          <span style={{ fontFamily: 'monospace' }}>
                            {conversation.order_id}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={cardOuterStyle}>
      <h2 style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', margin: '0 0 12px' }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#1a1a1a', wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 13, color: '#595959' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{value}</span>
    </div>
  );
}

function Badge({ text, color }: { text: string; color: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 500,
      background: `${color}15`,
      color: color,
      border: `1px solid ${color}40`,
    }}>
      {text}
    </span>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────

const cardOuterStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  border: '1px solid #f0f0f0',
  padding: 16,
};

const cardHeaderStyle: React.CSSProperties = {
  padding: '14px 16px',
  borderBottom: '1px solid #f0f0f0',
  fontSize: 14,
  fontWeight: 600,
  color: '#1a1a1a',
};

const channelItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: 10,
  background: '#fafafa',
  borderRadius: 6,
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '320px 1fr',
  gap: 16,
};

const backBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#595959',
  cursor: 'pointer',
  fontSize: 14,
  padding: '4px 0',
};

const loadingBoxStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  border: '1px solid #f0f0f0',
  padding: 40,
  textAlign: 'center',
  color: '#8c8c8c',
};

const errorBoxStyle: React.CSSProperties = {
  background: '#fff2f0',
  border: '1px solid #ffccc7',
  borderRadius: 8,
  padding: 16,
};

const errorLinkStyle: React.CSSProperties = {
  marginTop: 8,
  background: 'transparent',
  border: 'none',
  color: '#cf1322',
  cursor: 'pointer',
  textDecoration: 'underline',
  padding: 0,
  fontSize: 13,
};
